/**
 * Issue #28 — ratified mana / damage / cadence calibration authority.
 *
 * This module deliberately derives required mana from an already-legal combat
 * candidate. It is NOT an inverse `mana -> purchasable damage` currency and it
 * never bypasses V13 rarity / burst legality.
 */

import { NonFiniteNumericError } from '../math.js';
import { displacedBasicHits, type DisplacedBasicSource } from './displaced-basic.js';
import {
  V13_MID_REPEAT,
  masteryRealization,
  resolveLowRepeatAvailability,
  type LowRepeatAvailabilityPolicy,
  type SkillGrowthProfile,
} from './skill-damage-v13.js';
import { MAGIC_INFLUENCE_PROFILE_V1 } from './magic-source-v1.js';
import type { MagicInfluence } from '../types.js';

export const MANA_DAMAGE_V1_VERSION = 'mana-damage-v1-2026-08-24' as const;

export const MANA_DAMAGE_V1_PRICE = Object.freeze({
  incrementalReferenceE0: 2.2,
  concentrationBeta: 0.25,
  kappa: 6.0,
  sustainedIncrementalBasicDpsCeiling: 0.88,
  bridgeStartQ: 6.5,
  bridgeEndQ: 35.0,
  bridgeEndActionTimeS: 2.0,
  bridgeEndMinimumRecastS: 60.0,
});

export const MANA_DAMAGE_V1_MASTERY_ALPHA: Readonly<Record<SkillGrowthProfile, number>> = Object.freeze({
  current: 0.80,
  normal: 2 / 3,
  growth: 0.50,
});

export type CastMobility = 'MOBILE' | 'SLOWED' | 'STATIONARY';

export const MANA_DAMAGE_V1_FOCUS = Object.freeze({
  minimumQualifyingFocusS: 2.0,
  thresholdDiscount: Object.freeze({
    MOBILE: 0,
    SLOWED: 0.02,
    STATIONARY: 0.04,
  } satisfies Readonly<Record<CastMobility, number>>),
  maximumDiscount: Object.freeze({
    MOBILE: 0,
    SLOWED: 0.05,
    STATIONARY: 0.08,
  } satisfies Readonly<Record<CastMobility, number>>),
  /** Exact V1 exponential interpolation time constant. */
  saturationTauS: 4.0,
});

function requireFinite(value: number, name: string): void {
  if (!Number.isFinite(value)) throw new NonFiniteNumericError(`${name} must be finite (got ${value})`);
}

function requireFiniteNonNegative(value: number, name: string): void {
  requireFinite(value, name);
  if (value < 0) throw new Error(`${name} must be >= 0 (got ${value})`);
}

function requireFinitePositive(value: number, name: string): void {
  requireFinite(value, name);
  if (value <= 0) throw new Error(`${name} must be > 0 (got ${value})`);
}

/**
 * Maximum direct concentration that can co-realize on the same primary target
 * from one accepted activation. Component/damage-type boundaries never reduce
 * the concentration premium; mutually-exclusive branches are represented as
 * separate paths and therefore use the maximum path sum rather than a blind sum.
 */
export function aggregateActivationDirectConcentrationQ(
  coRealizablePaths: readonly (readonly number[])[],
): number {
  let maximum = 0;
  for (let pathIndex = 0; pathIndex < coRealizablePaths.length; pathIndex += 1) {
    const path = coRealizablePaths[pathIndex]!;
    let sum = 0;
    for (let componentIndex = 0; componentIndex < path.length; componentIndex += 1) {
      const q = path[componentIndex]!;
      requireFiniteNonNegative(q, `coRealizablePaths[${pathIndex}][${componentIndex}]`);
      sum += q;
    }
    maximum = Math.max(maximum, sum);
  }
  return maximum;
}

/**
 * Mana-priced direct-damage increment above the ordinary basic attacks the
 * action lock displaced. `displacedHitEquivalents` is `displacedBasicHits`
 * (`displaced-basic.ts`) of the displaced basic, in the same hit equivalents
 * as `qActivation`.
 */
function manaPricedIncrementalHitEquivalents(
  qActivation: number,
  displacedHitEquivalents: number,
): number {
  requireFiniteNonNegative(qActivation, 'qActivation');
  requireFiniteNonNegative(displacedHitEquivalents, 'displacedHitEquivalents');
  return Math.max(0, qActivation - displacedHitEquivalents);
}

/** Larger one-activation concentration is deliberately less mana-efficient. */
export function manaConcentrationFactor(e: number): number {
  requireFiniteNonNegative(e, 'e');
  const { incrementalReferenceE0: e0, concentrationBeta: beta } = MANA_DAMAGE_V1_PRICE;
  return 1 + beta * (Math.log(1 + e / e0) - Math.log(2));
}

/** Unrounded full-Mastery required-mana basis. */
export function rawFullMasteryManaCostFromIncrement(e: number): number {
  requireFiniteNonNegative(e, 'e');
  if (e === 0) return 0;
  return MANA_DAMAGE_V1_PRICE.kappa * e * manaConcentrationFactor(e);
}

/** Integer full-Mastery cost when no qualifying focus adjustment applies. */
export function fullMasteryManaCostFromIncrement(e: number): number {
  return Math.max(0, Math.round(rawFullMasteryManaCostFromIncrement(e)));
}

export function rawFullMasteryManaCostForCandidate(
  qActivation: number,
  displacedHitEquivalents: number,
): number {
  return rawFullMasteryManaCostFromIncrement(
    manaPricedIncrementalHitEquivalents(qActivation, displacedHitEquivalents),
  );
}

export function fullMasteryManaCostForCandidate(
  qActivation: number,
  displacedHitEquivalents: number,
): number {
  return Math.max(0, Math.round(rawFullMasteryManaCostForCandidate(qActivation, displacedHitEquivalents)));
}

export function fullMasteryManaCostForActivationPaths(
  coRealizablePaths: readonly (readonly number[])[],
  displacedHitEquivalents: number,
): number {
  return fullMasteryManaCostForCandidate(
    aggregateActivationDirectConcentrationQ(coRealizablePaths),
    displacedHitEquivalents,
  );
}

/**
 * Runtime mana factor. Canonical generation / burst legality still uses
 * MasteryLevel=10 and M=1.0; this function never returns generation credit.
 */
export function masteryManaFactor(masteryLevel: number, profile: SkillGrowthProfile): number {
  return Math.pow(masteryRealization(masteryLevel), MANA_DAMAGE_V1_MASTERY_ALPHA[profile]);
}

/**
 * Final integer realization for an already-unrounded full-Mastery basis when
 * no focus adjustment applies. Callers must not pre-round the input if they
 * need the Issue #28 single-final-round guarantee. Delegates to the MASTERY.1
 * single final-round boundary (`realizedManaCostFromMasteryBasis`) rather than
 * re-implementing the round/factor formula (STOP §4).
 */
export function masteryManaCost(
  rawFullMasteryCost: number,
  masteryLevel: number,
  profile: SkillGrowthProfile,
): number {
  return realizedManaCostFromMasteryBasis(rawFullMasteryCost, masteryLevel, profile);
}

/** The reference source's basic attack: its cycle and the recovery a skill may start in (`DisplacedBasicSource`). */
export type ReferenceBasic = Pick<DisplacedBasicSource, 'cycleS' | 'recoveryS'>;

function referenceBasicRate(referenceBasic: ReferenceBasic): number {
  requireFinitePositive(referenceBasic.cycleS, 'referenceBasic.cycleS');
  return 1 / referenceBasic.cycleS;
}

/**
 * The +0.88 BasicDPS sustained ceiling as a minimum recast: the increment
 * above the basic hits the action displaces (`displacedBasicHits`), at
 * `rho` of the reference basic's rate.
 */
export function sustainedIncrementalFloorS(q: number, actionS: number, referenceBasic: ReferenceBasic): number {
  requireFiniteNonNegative(q, 'q');
  const rho = MANA_DAMAGE_V1_PRICE.sustainedIncrementalBasicDpsCeiling;
  return Math.max(0, q - displacedBasicHits(actionS, referenceBasic)) / (rho * referenceBasicRate(referenceBasic));
}

/**
 * Current intermediate-reference bridge only. It reproduces the ratified
 * Lv60 Catalyst fixture and must not be read as a universal absolute cast-time
 * law for every weapon family.
 */
export function intermediateBridgeActionTimeS(q: number, referenceAttackRatePerS: number): number {
  requireFinitePositive(referenceAttackRatePerS, 'referenceAttackRatePerS');
  const { bridgeStartQ, bridgeEndQ, bridgeEndActionTimeS } = MANA_DAMAGE_V1_PRICE;
  if (q < bridgeStartQ || q > bridgeEndQ) {
    throw new Error(`q must be in [${bridgeStartQ}, ${bridgeEndQ}] for the intermediate bridge (got ${q})`);
  }
  const startActionS = V13_MID_REPEAT.actionCycles / referenceAttackRatePerS;
  const u = (q - bridgeStartQ) / (bridgeEndQ - bridgeStartQ);
  return startActionS + (bridgeEndActionTimeS - startActionS) * u;
}

/**
 * Minimum recast for the Mid -> 35H reference bridge. Uses the stronger of
 * the existing +0.88 BasicDPS sustained ceiling and the concentration floor
 * that joins Mid to 35H -> 60s.
 */
export function intermediateBridgeMinimumRecastS(q: number, referenceBasic: ReferenceBasic): number {
  const referenceAttackRatePerS = referenceBasicRate(referenceBasic);
  const { bridgeStartQ, bridgeEndQ, bridgeEndMinimumRecastS } = MANA_DAMAGE_V1_PRICE;
  const actionS = intermediateBridgeActionTimeS(q, referenceAttackRatePerS);
  const sustainedFloorS = sustainedIncrementalFloorS(q, actionS, referenceBasic);

  const tMid = V13_MID_REPEAT.recastCycles / referenceAttackRatePerS;
  const k = Math.log(bridgeEndMinimumRecastS / tMid) / (bridgeEndQ - bridgeStartQ);
  const concentrationFloorS = tMid * Math.exp(k * (q - bridgeStartQ));
  return Math.max(sustainedFloorS, concentrationFloorS);
}

export function intermediateBridgeFullMasteryManaCost(q: number, referenceBasic: ReferenceBasic): number {
  const actionS = intermediateBridgeActionTimeS(q, referenceBasicRate(referenceBasic));
  return fullMasteryManaCostForCandidate(q, displacedBasicHits(actionS, referenceBasic));
}

/**
 * V13 owns the absolute low-repeat floor at 35H_ref+. Source-relative timing
 * may make the realized interval slower, never faster than that floor.
 */
export function resolveEffectiveLowRepeatAvailability(
  nominalQRef: number,
  sourceRelativeRealizedRecastS: number,
): LowRepeatAvailabilityPolicy {
  requireFinitePositive(sourceRelativeRealizedRecastS, 'sourceRelativeRealizedRecastS');
  const v13 = resolveLowRepeatAvailability(nominalQRef);
  const effective = Math.max(sourceRelativeRealizedRecastS, v13.minimumAvailabilityIntervalS);
  if (v13.kind === 'rolling_charge') {
    return Object.freeze({
      kind: 'rolling_charge',
      minimumAvailabilityIntervalS: effective,
      capacity: 1,
      rollingRechargeS: Math.max(v13.rollingRechargeS ?? v13.minimumAvailabilityIntervalS, effective),
    });
  }
  return Object.freeze({
    kind: 'ordinary_cooldown',
    minimumAvailabilityIntervalS: effective,
  });
}

/**
 * Small execution-risk adjustment. Ordinary action time alone earns nothing:
 * focus must be >=2s AND movement must be SLOWED/STATIONARY.
 */
export function longFocusManaDiscount(focusTimeS: number, mobility: CastMobility): number {
  requireFiniteNonNegative(focusTimeS, 'focusTimeS');
  const { minimumQualifyingFocusS, thresholdDiscount, maximumDiscount, saturationTauS } = MANA_DAMAGE_V1_FOCUS;
  if (mobility === 'MOBILE' || focusTimeS < minimumQualifyingFocusS) return 0;

  const base = thresholdDiscount[mobility];
  const cap = maximumDiscount[mobility];
  const excess = focusTimeS - minimumQualifyingFocusS;
  return base + (cap - base) * (1 - Math.exp(-excess / saturationTauS));
}

/** Raw focus-adjusted basis; no integer rounding occurs here. */
export function applyLongFocusManaAdjustmentRaw(
  rawRequiredMana: number,
  focusTimeS: number,
  mobility: CastMobility,
): number {
  requireFiniteNonNegative(rawRequiredMana, 'rawRequiredMana');
  return rawRequiredMana * (1 - longFocusManaDiscount(focusTimeS, mobility));
}

/**
 * Standalone M10 final integer charge after focus. Do not chain this rounded
 * value into lower-Mastery realization; use realizedManaCostFromRawBase().
 */
export function applyLongFocusManaAdjustment(
  rawRequiredMana: number,
  focusTimeS: number,
  mobility: CastMobility,
): number {
  return Math.max(0, Math.round(applyLongFocusManaAdjustmentRaw(rawRequiredMana, focusTimeS, mobility)));
}

/**
 * SOURCE.2 (source-weapon-technique-hybrid-contract.md §23/§27): the
 * self-containment authored-mana factor. Exact frozen seeds
 * LOW=1.30/MEDIUM=1.15/HIGH=1.00 — reads the centralized
 * `MAGIC_INFLUENCE_PROFILE_V1` constant, no duplicate literal here. Never
 * itself rounds; it is one raw multiplicative stage among several composed
 * in `realizedManaCostFromRawBase` before that function's single final
 * round (STOP §4: never `round(base) * factor` followed by a second round).
 */
export function magicInfluenceManaMultiplier(influence: MagicInfluence): number {
  return MAGIC_INFLUENCE_PROFILE_V1[influence].authoredManaMultiplier;
}

/**
 * MASTERY.1 (Stage A Task 2) — the raw pre-Mastery mana boundary: every
 * current non-Mastery raw adjustment (eligible focus discount, then
 * MagicInfluence authored-mana factor for MAGIC candidates only) applied to
 * an unrounded full-Mastery basis, with NO rounding performed here. This is
 * exactly the value MASTERY.1 canonical Skills persist as
 * `SkillMasteryPolicyV1.rawManaBeforeMastery` (src/types.ts) so runtime can
 * realize lower Mastery without recomputing q/cadence/reference pricing.
 * Issue #28 canonical calculation order, amended by SOURCE.2 §27: raw base ->
 * eligible focus adjustment -> MagicInfluence authored-mana factor (only for
 * a SourceClass=MAGIC candidate; absent for WEAPON/BODY, S2-R44).
 */
export function rawManaBeforeMastery(
  rawFullMasteryCost: number,
  focusTimeS = 0,
  mobility: CastMobility = 'MOBILE',
  magicInfluence?: MagicInfluence,
): number {
  requireFiniteNonNegative(rawFullMasteryCost, 'rawFullMasteryCost');
  const adjustedRaw = applyLongFocusManaAdjustmentRaw(rawFullMasteryCost, focusTimeS, mobility);
  return magicInfluence !== undefined ? adjustedRaw * magicInfluenceManaMultiplier(magicInfluence) : adjustedRaw;
}

/**
 * MASTERY.1 (Stage A Task 2) — the single final-round boundary consuming the
 * persisted `rawManaBeforeMastery` basis. Never chain a second round; never
 * recompute the basis from a rounded M10 integer (STOP §4 single-final-round
 * guarantee).
 */
export function realizedManaCostFromMasteryBasis(
  rawManaBeforeMasteryValue: number,
  masteryLevel: number,
  profile: SkillGrowthProfile,
): number {
  requireFiniteNonNegative(rawManaBeforeMasteryValue, 'rawManaBeforeMasteryValue');
  if (rawManaBeforeMasteryValue === 0) return 0;
  return Math.max(0, Math.round(rawManaBeforeMasteryValue * masteryManaFactor(masteryLevel, profile)));
}

/**
 * Issue #28 canonical calculation order, amended by SOURCE.2 §27:
 * raw base -> eligible focus adjustment -> MagicInfluence authored-mana
 * factor (only for a SourceClass=MAGIC candidate; absent for WEAPON/BODY,
 * S2-R44) -> Mastery realization -> one final round. Delegates through the
 * MASTERY.1 raw-basis boundary (`rawManaBeforeMastery` /
 * `realizedManaCostFromMasteryBasis`) — this function's own numeric output
 * is unchanged, only its implementation is decomposed.
 */
export function realizedManaCostFromRawBase(
  rawFullMasteryCost: number,
  masteryLevel: number,
  profile: SkillGrowthProfile,
  focusTimeS = 0,
  mobility: CastMobility = 'MOBILE',
  magicInfluence?: MagicInfluence,
): number {
  requireFiniteNonNegative(rawFullMasteryCost, 'rawFullMasteryCost');
  const basis = rawManaBeforeMastery(rawFullMasteryCost, focusTimeS, mobility, magicInfluence);
  return realizedManaCostFromMasteryBasis(basis, masteryLevel, profile);
}

export function realizedManaCostForCandidate(
  qActivation: number,
  displacedHitEquivalents: number,
  masteryLevel: number,
  profile: SkillGrowthProfile,
  focusTimeS = 0,
  mobility: CastMobility = 'MOBILE',
  magicInfluence?: MagicInfluence,
): number {
  return realizedManaCostFromRawBase(
    rawFullMasteryManaCostForCandidate(qActivation, displacedHitEquivalents),
    masteryLevel,
    profile,
    focusTimeS,
    mobility,
    magicInfluence,
  );
}
